import React, { useMemo } from 'react';

import cn from 'classnames';
import Image from 'next/legacy/image';

import PriceChart from '@components/Charts/PriceChart';
import Icon from '@components/Icon';
import { formatPrettyValue, I18n } from '@utils/index';

import styles from './index.module.less';

export type zoneDetail = {
  categoryName: string;
  categoryKey: string; // 板块code
  coinAndKeys: {
    // 板块关联的币种code
    ALGO: string;
    XRP: string;
  };
  picPaths: string[];
  leadingCurrency: string; // 领涨/领跌币种
  maxRiseAndFall: number; // 领涨/领跌币种的涨幅
  zoneRiseAndFall: number; // 板块平均涨幅
  order: number; // 板块排序
  status: number; // 板块状态
  created: number; // 板块创建时间
  modified: number; // 板块修改时间
  riseCount: number; // 板块内币种涨的数量
  fallCount: number; // 板块内币种跌的数量
  bars: string[]; // kline bar
};

export interface RiseFallCardProps {
  zoneDetail: zoneDetail;
  zoneDetailList: any[];
}

export const RiseFallCardList: React.FC<RiseFallCardProps> = (props) => {
  const { zoneDetailList, zoneDetail: { bars } = {} } = props;

  const _bars = useMemo(() => {
    let _bars = bars?.map((item) => {
      const itemArr = item.split(',');
      return Number(itemArr[0]);
    }) ?? [];
    if (!_bars.length) {
      _bars = [0, 0];
    }
    if (_bars.length === 1) {
      _bars.push(_bars[0]);
    }

    // list 更改为正数
    const minNum = Math.min(..._bars)
    if (minNum < 0) {
      _bars = _bars.map(i => i + Math.abs(minNum))
    }
    return _bars
  }, [bars])

  const {
    riseNum,
    fallNum,
    riseFall,
    maxRiseFallItem
  } = zoneDetailList?.reduce(
    (pre, cur) => {
      const _pre = { ...pre };
      if (cur.riseAndFall >= 0) {
        _pre.riseNum += 1;
      } else {
        _pre.fallNum += 1;
      }
      if (Math.abs(cur.riseAndFall) > Math.abs(_pre.maxRiseFallItem.riseAndFall || 0)) {
        _pre.maxRiseFallItem = cur;
      }
      _pre.riseFall += Number(cur.riseAndFall);
      return _pre;
    },
    {
      riseNum: 0, // 涨的个数
      fallNum: 0, // 跌的个数
      riseFall: 0, // 涨跌总和
      maxRiseFallItem: {} // 涨幅最大项,跌幅最大项
    }
  );

  const _riseFall = (riseFall / zoneDetailList.length).toFixed(2);
  const { coinCode, formatPriceLast, riseAndFall = 0, picturePath } = maxRiseFallItem;
  const coinArr = coinCode?.split('_') ?? [];
  const currencyText = coinArr.length ? `${coinArr[0]}/${coinArr[1]}` : '--';
  const priceNum = formatPriceLast ? formatPrettyValue(formatPriceLast) : '--';
  const riseFallNum = `${riseAndFall > 0 ? '+' : ''}${riseAndFall}%`;
  const riseFallText = riseAndFall >= 0 ? I18n.t('YP3V7wTT') : I18n.t('8nsJLI7n');

  const leftWidth = `${riseNum + fallNum > 0 ? riseNum / (riseNum + fallNum) * 100 : 50}%`
  const rightWidth = `${riseNum + fallNum > 0 ? fallNum / (riseNum + fallNum) * 100 : 50}%`

  return (
    <div className={styles.box}>
      <div className={styles.container}>
        <div className={cn(styles.card, styles.largeCard)}>
          <div className={cn(styles.title, 'DINPro')}>{I18n.t('Hi2bMmV7')}</div>
          <div>
            <div className={styles.riseFallData}>
              <div className={styles.riseNumber}>
                {I18n.t('xrbyIWPf')}
                <span className='DINPro-Medium'>
                  {riseNum}
                  <Icon name='riseFall' className={styles.riseFallIcon} />
                </span>
              </div>
              <div className={styles.fallNumber}>
                {I18n.t('AoqepZSg')}
                <span className='DINPro-Medium'>
                  {fallNum}
                  <Icon
                    name='riseFall'
                    className={cn(styles.riseFallIcon, styles.fallIcon)}
                  />
                </span>
              </div>
            </div>
            <div className={styles.percentNum}>
              <div className={cn(styles.left)} style={{ width: leftWidth }} ></div>
              <div className={cn(styles.right)} style={{ width: rightWidth }} ></div>
            </div>
          </div>
        </div>
        <div className={cn(styles.card, styles.middleCard)}>
          <div className={cn(styles.title, 'DINPro')}>{I18n.t('ULc7tkY8')}</div>
          <div className={styles.wholeRiseFallData}>
            <div
              className={cn(styles['wholeRiseFallData-num'], 'DINPro-Medium', {
                [styles.riseTextColor]: Number(_riseFall) > 0
              })}
            >
              {isNaN(Number(_riseFall)) ? '--' : `${(Number(_riseFall)) > 0 ? '+' : ''}${_riseFall}%`}
            </div>
            <div className={styles['wholeRiseFallData-svg']} id='svg'>
              <PriceChart
                data={_bars}
                riseAndFall={Number(_riseFall)}
                height={61}
                style={{ overflow: 'unset' }}
              />
            </div>
          </div>
        </div>
        <div className={cn(styles.card, styles.middleCard)}>
          <div
            className={cn(styles.title, 'DINPro')}
          >
            {riseFallText}
          </div>
          <div className={styles.maxRiseData}>
            <div className={styles.currencyInfo}>
              <Image width={32} height={32} alt='kikitrade' src={picturePath} />
              <div className={styles.currencyContainer}>
                <div className={cn(styles.currency, 'DINPro')}>
                  {currencyText}
                </div>
                <div className={cn(styles.price, 'DINPro')}>{priceNum}</div>
              </div>
            </div>
            <div className={cn(styles.maxRiseNum, 'DINPro-Medium', {
              [styles.fallTextColor]: riseAndFall < 0
            })}>
              {riseFallNum}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
